import { $, $$, body } from "@utils/dom";
import { on, off } from "@utils/listener";
import { STATE } from "@core/state";
import Wishlist from "@core/wishlist";
import * as cart from '@vendors/shopify/theme-cart/theme-cart';

export const CLASSNAME = "--js-site-wishlist-opened";
export const CLOSING_CLASSNAME = "--js-site-wishlist-closing";

class SiteWishlist {
  constructor(el, emitter) {
    this.el = el;
    this.emitter = emitter;

    this.initialized = false;

    this._opened = false;

    this._handleTriggers = this._handleTriggers.bind(this);
    this._handleKeyDown = this._handleKeyDown.bind(this);
    this._onCloseCompleted = this._onCloseCompleted.bind(this);

    this._render = this._render.bind(this);
    this._fetch = this._fetch.bind(this);

    this.open = this.open.bind(this);
    this.close = this.close.bind(this);

    this._update = this._update.bind(this);
    this._updateState = this._updateState.bind(this);
  }

  get name() {
    return `SiteWishlist`;
  }

  init() {
    this.initialized = true;

    this._opened = false;
    this._count  = this.el.dataset.initialCount ? parseInt(this.el.dataset.initialCount) : 0;

    this.wishlistItems = null;

    this.triggers = [...$$(`[aria-controls="${this.el.id}"]`)];
    this.container = $('.site-wishlist__container', this.el);
    this.content = $('.site-wishlist__content', this.el);

    this.counters = [...$$('[data-wishlist-counter]')];

    this._fetch();
    this._bindEvents();
  }

  destroy() {
    body.classList.remove(CLASSNAME);
    this._clearCartUI();
    this._unbindEvents();

    this.el = null;
    this.triggers = null;
    this.container = null;
    this.content = null;

    this.counters = null;
    this._opened = null;
    this._count = null;

    this.initialized = false;
  }

  _bindEvents() {
    if (this.triggers) on(this.triggers, "click", this._handleTriggers);
    on(window, "keydown", this._handleKeyDown);

    this.emitter.on("SiteWishlist.toggle", this._handleTriggers);
    this.emitter.on("SiteWishlist.update", this._update);

    this.emitter.on("SiteNav.open", this.close);
    this.emitter.on("SiteCart.open", this.close);
  }
  _unbindEvents() {
    if (this.triggers) off(this.triggers, "click", this._handleTriggers);
    off(window, "keydown", this._handleKeyDown);

    this.emitter.off("SiteWishlist.toggle", this._handleTriggers);
    this.emitter.off("SiteWishlist.update", this._update);

    this.emitter.off("SiteNav.open", this.close);
    this.emitter.off("SiteCart.open", this.close);
  }

  _fetch(callback = null){
    fetch(`${window.Shopify.routes.root ? window.Shopify.routes.root : '/'}?view=wishlist`)
    .then((response) => {
      if(response.ok) {
        return response.text()
      }
      return Promise.reject(response);
    })
    .then(response => {
      this._render(response);
      if(callback) callback();
    });
  }

  _render(html){
    this._clearCartUI();

    this.content.innerHTML = html;

    this.wishlistItems = [...$$('.site-wishlist__item', this.el)].map(el => new WishlistItem(el, this.emitter));
  }

  _clearCartUI(){
    if (this.wishlistItems) this.wishlistItems.forEach(el => el.destroy());

    this.wishlistItems = null;
  }

  _update(state){
    if (state) {
      window.WishlistObj = state.data;
    }

    this._fetch();
    this._updateState(state);

    this.emitter.emit("SiteWishlist.updated");
  }

  _updateState(state){
    if (!this.counters) return;

    this.counters.forEach((counter)=> {
      counter.classList.add('--is-updating'); 
    });

    if (state && state.data) {
      this._count = state.data.length;

      if(this._count){
        this.el.classList.remove('--is-empty');
      } else {
        this.el.classList.add('--is-empty');
      }

      this.counters.forEach((counter)=> {
        counter.innerHTML = '';

        if(this._count){
          let span = document.createElement('span');
          span.innerHTML = this._count;
          counter.append(span);
          counter.classList.remove('--is-empty');
        } else {
          counter.classList.add('--is-empty');
        }

        counter.classList.remove('--is-updating');
      });
    }
  }

  open() {
    // if SiteWishlist is already opened, do nothing
    if (this._opened === true) return;
    this._opened = true;

    off(this.container, 'transitionend',this._onCloseCompleted);

    // dispatch to state current status
    STATE.dispatch("SITE_WISHLIST", this._opened);

    // inform <body> that SiteWishlist is opened
    body.classList.add(CLASSNAME);
    body.classList.remove(CLOSING_CLASSNAME);

    // stop page scroll & site-wishlist open
    this.emitter.emit("SiteScroll.stop", true);
    this.emitter.emit("SiteWishlist.open");
    this.emitter.emit("DataLayer.view_wishlist");

    // inform each triggers that SiteWishlist is opened
    this.triggers.forEach(btn => {
      btn.setAttribute("aria-expanded", true);
      btn.classList.add("is-active");
    });

    // set SiteWishlist visible for screen readers
    this.el.setAttribute("aria-hidden", false);

    this.el.classList.add('--js-anim-playing');
  }
  close() {
    // if SiteWishlist isn't opened, do nothing
    if (this._opened === false) return;
    this._opened = false;

    // dispatch to state current status
    STATE.dispatch("SITE_WISHLIST", this._opened);

    // inform triggers that SiteWishlist is closed
    this.triggers.forEach((btn) => {
      btn.setAttribute("aria-expanded", false);
      btn.classList.remove("is-active");
    });

    // inform <body> that SiteWishlist is closed
    body.classList.remove(CLASSNAME);
    body.classList.add(CLOSING_CLASSNAME);

    // restore previous scrollY
    window.scrollTo({ top: this._scrollY, behavior: "auto" });

    // site-wishlist close
    this.emitter.emit("SiteWishlist.close");

    // close callback (should be call at animation's end)
    on(this.container, "transitionend", this._onCloseCompleted);
  }
  toggle() {
    // regular SiteWishlist toggle handling
    if (this._opened === true) this.close();
    else this.open();
  }

  _handleTriggers(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.toggle();
  }
  _handleKeyDown(event) {
    if (this._opened === true && (event.key === "Escape" || event.key === "Esc")) {
      this.close();
    }
  }
  _onCloseCompleted() {
    if (this._opened === true) return;

    body.classList.remove(CLOSING_CLASSNAME);

    // restart page scroll
    if (!STATE['siteCartOpen']) this.emitter.emit("SiteScroll.start");

    // hide SiteWishlist from screen readers
    this.el.setAttribute("aria-hidden", true);

    this.el.classList.remove('--js-anim-playing');
  }

  // getters
  get opened() {
    return this._opened;
  }
}


class WishlistItem {
  constructor(el, emitter) {

    this.el = el;
    this.emitter = emitter;

    this._removeItem = this._removeItem.bind(this);
    this._onError = this._onError.bind(this);
    this._addItem = this._addItem.bind(this);

    this.init();
  }

  init(){
    this.customerId = this.el.getAttribute('data-customer-id');
    this.productHandle = this.el.getAttribute('data-product-handle');
    this.variantId = parseInt(this.el.getAttribute('data-variant-id'));

    this.error = $(`.site-wishlist__itemError`, this.el);
    this.remove = $('[data-remove]',this.el);
    this.addToCartBtn = $('[data-add-to-cart]',this.el);

    this._bindEvents();
  }

  destroy(){
    this._unbindEvents();

    this.el = null;
    this.emitter = null;

    this.customerId  = null;
    this.productHandle  = null;
    this.variantId  = null;

    this.error = null;
    this.remove = null;

    this._removeItem = null;
    this._onError = null;
    this._addItem = null;

    this.addToCartBtn = null;
  }

  _bindEvents(){
    if (this.remove) on(this.remove, "click", this._removeItem);
    if (this.addToCartBtn) on(this.addToCartBtn, "click", this._addItem);
  }

  _unbindEvents(){
    if (this.remove) off(this.remove, "click", this._removeItem);
    if (this.addToCartBtn) off(this.addToCartBtn, "click", this._addItem);
  }



  _removeItem(){
    Wishlist.delete(this.customerId, this.productHandle, this.variantId).then(state => {
      this.emitter.emit('SiteWishlist.update', state);
    });
  }

  _addItem(event){
    if(this.error) {
      this.error.innerHTML = "";
    }

    const id = parseInt(event.currentTarget.dataset.addToCart);

    // submit
    cart.addItem(id).then((item) => {
      this.emitter.emit('SiteWishlist.update');
      this.emitter.emit("DataLayer.add_to_wishlist", item);
    }).catch(this._onError);
  }

  _onError(error) {

    let errorElement = null;

    if(this.error) {
      errorElement = this.error;
    } else {
      errorElement = document.createElement('div');
      errorElement.classList.add(`site-wishlist__itemError`);
      this.error = errorElement;
    }

    // get error messages, if any
    error.json().then((json) => {
      if(json.description) this.error.innerHTML = `<p><strong>${json.message} :</strong> ${json.description}</p>`;
    });

    // append error
    this.el.appendChild(errorElement);
  }



}

export default SiteWishlist;
